import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { resolveBranchForPrint } from './branchPrint';

const csvCell = (value) => {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const csvRow = (cells = []) => cells.map(csvCell).join(',');

export const dash = (value) => {
  if (value == null || value === '') return '-';
  return String(value);
};

export const fmtExportDate = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return String(dateString);
  return d.toLocaleDateString('en-GB');
};

export const fmtExportDateTime = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return String(dateString);
  return d.toLocaleString('en-GB');
};

export const fmtExportMoney = (amount) =>
  `Rs. ${Number(amount || 0).toLocaleString('en-LK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

export const fmtExportStatus = (status) =>
  String(status || '-')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const cellValue = (column, row) => {
  if (typeof column.value === 'function') return column.value(row);
  const raw = row?.[column.key];
  return raw == null || raw === '' ? '-' : raw;
};

const downloadBlob = (content, mime, filename) => {
  const blob = new Blob([content], { type: mime });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

const datedFilename = (filename, ext) => {
  const base = (filename || 'export').replace(/\.(csv|pdf)$/i, '');
  const day = new Date().toISOString().split('T')[0];
  return `${base}_${day}.${ext}`;
};

const buildCsvBody = ({ title, summary, columns, rows, extraSections }) => {
  const lines = [];
  if (title) lines.push(csvRow([title]));
  if (summary?.length) {
    summary.forEach((item) => lines.push(csvRow([item.label, item.value])));
    lines.push('');
  }
  if (columns?.length) {
    lines.push(csvRow(columns.map((c) => c.label)));
    (rows || []).forEach((row) => {
      lines.push(csvRow(columns.map((c) => cellValue(c, row))));
    });
  }
  (extraSections || []).forEach((section) => {
    lines.push('');
    if (section.title) lines.push(csvRow([section.title]));
    if (section.summary?.length) {
      section.summary.forEach((item) => lines.push(csvRow([item.label, item.value])));
    }
    if (section.columns?.length) {
      lines.push(csvRow(section.columns.map((c) => c.label)));
      (section.rows || []).forEach((row) => {
        lines.push(csvRow(section.columns.map((c) => cellValue(c, row))));
      });
    }
  });
  return `\uFEFF${lines.join('\n')}`;
};

export const exportTableCsv = (options = {}) => {
  const { filename = 'export', title, summary, columns, rows, extraSections } = options;
  const content = buildCsvBody({ title, summary, columns, rows, extraSections });
  downloadBlob(content, 'text/csv;charset=utf-8;', datedFilename(filename, 'csv'));
};

const lastTableY = (doc, fallback) =>
  doc.lastAutoTable?.finalY ||
  (typeof doc.getLastAutoTable === 'function' ? doc.getLastAutoTable()?.finalY : null) ||
  fallback;

const ensureSpace = (doc, y, needed = 24) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + needed > pageHeight - 14) {
    doc.addPage();
    return 16;
  }
  return y;
};

const drawTable = (doc, columns, rows, startY, sectionTitle) => {
  let y = startY;
  if (sectionTitle) {
    y = ensureSpace(doc, y, 18);
    doc.setFontSize(11);
    doc.setTextColor(21, 101, 192);
    doc.text(sectionTitle, 14, y);
    y += 5;
  }
  if (!columns?.length) return y;
  autoTable(doc, {
    startY: y,
    head: [columns.map((c) => c.label)],
    body: (rows || []).length
      ? rows.map((row) => columns.map((c) => String(cellValue(c, row))))
      : [columns.map(() => '')],
    styles: { fontSize: 8, cellPadding: 1.6, overflow: 'linebreak' },
    headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 251, 255] },
    columnStyles: Object.fromEntries(
      columns.map((c, i) => [i, c.align === 'right' ? { halign: 'right' } : {}])
    ),
    margin: { left: 14, right: 14 }
  });
  return lastTableY(doc, y) + 8;
};

export const exportTablePdf = (options = {}) => {
  const {
    title = 'Export',
    subtitle,
    filename,
    columns = [],
    rows = [],
    summary = [],
    extraSections = [],
    landscape = false
  } = options;

  const branch = resolveBranchForPrint(options);
  const generated = new Date().toLocaleString('en-GB');
  const recordCount =
    (rows || []).length ||
    extraSections.reduce((n, section) => n + ((section.rows || []).length), 0);

  const doc = new jsPDF({
    orientation: landscape ? 'landscape' : 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  let y = 16;
  doc.setFontSize(15);
  doc.setTextColor(21, 101, 192);
  doc.text(title, 14, y);
  y += 6;

  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  [
    subtitle,
    `Records: ${recordCount}`,
    `Branch: ${branch.label}`,
    `Generated: ${generated}`
  ]
    .filter(Boolean)
    .forEach((line) => {
      doc.text(String(line), 14, y);
      y += 4.2;
    });

  if (summary.length) {
    autoTable(doc, {
      startY: y + 2,
      theme: 'grid',
      body: summary.map((item) => [item.label, String(item.value ?? '')]),
      styles: { fontSize: 8, cellPadding: 1.8 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 55, fillColor: [227, 242, 253] }
      },
      margin: { left: 14, right: 14 }
    });
    y = lastTableY(doc, y) + 8;
  } else {
    y += 4;
  }

  y = drawTable(doc, columns, rows, y);
  extraSections.forEach((section) => {
    if (section.summary?.length) {
      y = ensureSpace(doc, y, 20);
      if (section.title) {
        doc.setFontSize(11);
        doc.setTextColor(21, 101, 192);
        doc.text(section.title, 14, y);
        y += 5;
      }
      autoTable(doc, {
        startY: y,
        theme: 'grid',
        body: section.summary.map((item) => [item.label, String(item.value ?? '')]),
        styles: { fontSize: 8, cellPadding: 1.8 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
        margin: { left: 14, right: 14 }
      });
      y = lastTableY(doc, y) + 8;
      y = drawTable(doc, section.columns, section.rows, y);
    } else {
      y = drawTable(doc, section.columns, section.rows, y, section.title);
    }
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.text('Sun System ERP · Confidential', 14, pageHeight - 8);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
  }

  doc.save(datedFilename(filename || 'export', 'pdf'));
};
